import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Users,
    ShieldCheck,
    Ban,
    Boxes,
    Activity,
    BarChart3,
    AlertTriangle,
    ArrowRight,
    UserMinus,
} from "lucide-react";
import StatCard from "../../components/primitives/StatCard";
import Sparkline from "../../components/primitives/Sparkline";
import { admin, ApiError } from "../../services/api";
import "../../styles/admin-shell.css";

function AdminDashboardPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const ctrl = new AbortController();
        (async () => {
            try {
                const data = await admin.stats({ signal: ctrl.signal });
                if (!cancelled) setStats(data);
            } catch (err) {
                if (cancelled || err?.name === "AbortError") return;
                setError(err instanceof ApiError ? err.message : err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            ctrl.abort();
        };
    }, []);

    return (
        <div className="admin-page">
            <header className="admin-page__header">
                <div className="admin-page__title-block">
                    <span className="admin-page__eyebrow uppercase-label">
                        Admin / Dashboard
                    </span>
                    <h1 className="admin-page__title">Backoffice console</h1>
                    <p className="admin-page__subtitle">
                        Headline metrics, recent activity, quick links to user and event
                        tooling.
                    </p>
                </div>
            </header>

            <div className="admin-page__body">
                {error && (
                    <div className="admin-error" role="alert">
                        <AlertTriangle size={14} aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                )}

                {loading && !stats ? (
                    <div className="admin-loading">Loading stats…</div>
                ) : stats ? (
                    <>
                        <section className="admin-section">
                            <header className="admin-section__head">
                                <h2 className="admin-section__title">Users</h2>
                            </header>
                            <div className="admin-stats">
                                <StatCard
                                    label="Total users"
                                    value={stats.total_users}
                                    icon={Users}
                                />
                                <StatCard
                                    label="Verified"
                                    value={stats.verified_users}
                                    icon={ShieldCheck}
                                    tone="success"
                                    hint={`${stats.total_users - stats.verified_users} unverified`}
                                />
                                <StatCard
                                    label="Admins"
                                    value={stats.admin_users}
                                    icon={ShieldCheck}
                                    tone="accent"
                                />
                                <StatCard
                                    label="Disabled"
                                    value={stats.disabled_users}
                                    icon={Ban}
                                    tone={stats.disabled_users > 0 ? "danger" : "default"}
                                />
                                <StatCard
                                    label="Anonymised"
                                    value={stats.anonymised_users}
                                    icon={UserMinus}
                                    tone="default"
                                />
                            </div>
                        </section>

                        <section className="admin-section">
                            <header className="admin-section__head">
                                <h2 className="admin-section__title">Activity</h2>
                            </header>
                            <div className="admin-stats">
                                <StatCard
                                    label="Active SKUs"
                                    value={stats.total_skus_active}
                                    icon={Boxes}
                                    tone="success"
                                />
                                <StatCard
                                    label="Packs · 7d"
                                    value={stats.total_packs_last_7d}
                                    icon={Activity}
                                    tone="accent"
                                />
                                <StatCard
                                    label="Packs · 30d"
                                    value={stats.total_packs_last_30d}
                                    icon={BarChart3}
                                    tone="accent"
                                />
                            </div>
                        </section>

                        <section className="admin-section">
                            <header className="admin-section__head">
                                <h2 className="admin-section__title">
                                    Daily packs · last 30 days
                                </h2>
                                <span className="admin-section__hint">
                                    {stats.total_packs_last_30d} runs
                                </span>
                            </header>
                            <div className="admin-sparkline-card">
                                <Sparkline
                                    series={stats.daily_packs_last_30d || []}
                                    days={30}
                                    height={120}
                                />
                            </div>
                        </section>

                        <section className="admin-section">
                            <header className="admin-section__head">
                                <h2 className="admin-section__title">Tools</h2>
                            </header>
                            <div className="admin-quick-links">
                                <Link to="/admin/users" className="admin-quick-link">
                                    <span className="admin-quick-link__icon" aria-hidden="true">
                                        <Users size={18} />
                                    </span>
                                    <div className="admin-quick-link__text">
                                        <div className="admin-quick-link__title">Users</div>
                                        <div className="admin-quick-link__desc">
                                            Search, view, verify, disable, promote.
                                        </div>
                                    </div>
                                    <ArrowRight
                                        size={16}
                                        className="admin-quick-link__arrow"
                                        aria-hidden="true"
                                    />
                                </Link>
                                <Link to="/admin/events" className="admin-quick-link">
                                    <span className="admin-quick-link__icon" aria-hidden="true">
                                        <Activity size={18} />
                                    </span>
                                    <div className="admin-quick-link__text">
                                        <div className="admin-quick-link__title">Audit log</div>
                                        <div className="admin-quick-link__desc">
                                            Every meaningful event, filterable.
                                        </div>
                                    </div>
                                    <ArrowRight
                                        size={16}
                                        className="admin-quick-link__arrow"
                                        aria-hidden="true"
                                    />
                                </Link>
                            </div>
                        </section>
                    </>
                ) : null}
            </div>
        </div>
    );
}

export default AdminDashboardPage;
